import type { NetworkDashboardAvailableStore } from '@gozoki/api-types'
import { DateTime } from 'luxon'
import type { DateRangePreset } from '~/components/ui/date-range-picker'

// Fichier volontairement pur (luxon uniquement + `import type`, jamais un import `~` en
// valeur) : `date-shortcuts.test.mjs` le charge via `node --test` sans transform TSX/JSX ni
// résolution de l'alias `~`. `~/components/ui/date-range-picker` (React + react-day-picker)
// et `~/components/dashboards/filters/*` ne peuvent donc pas être importés ici en valeur —
// leurs `type`s le peuvent (`import type` est effacé avant résolution du module, cf. rapport)
// mais `DEFAULT_PRESETS`, `getRangeForPreset`, `resolveAllTimeStartIso`, `comparisonShiftDays`
// et `DASHBOARD_ALL_TIME_START_ISO` sont donc réimplémentés/dupliqués localement ci-dessous.

// Presets réseau (mêmes valeurs que `HR_DATE_PRESETS` côté RH) : le picker de la modale
// n'affiche que `DASHBOARD_DATE_PRESETS` (sans « Aujourd'hui »), mais l'URL peut porter
// n'importe quel slug standard.
export const STORE_DASHBOARD_DATE_PRESETS = [
  'yesterday',
  'today',
  '7d',
  '30d',
  '3m',
  '12m',
  'mtd',
  'ytd',
  'all',
] as const satisfies readonly DateRangePreset[]

export interface DateWindow {
  start: string
  end: string
}

export interface DashboardWindows {
  period: DateWindow
  comparison: DateWindow
}

/**
 * Doit rester égale à `DASHBOARD_ALL_TIME_START_ISO`
 * (`~/components/dashboards/filters/types`) — dupliquée ici pour garder ce fichier
 * testable en `node --test` sans import `~` en valeur. Marque « Toujours » dans l'URL
 * (`startDate`), résolue en date réelle par `resolveStoreDashboardWindows` ci-dessous.
 */
export const STORE_DASHBOARD_ALL_TIME_SENTINEL = '2020-01-01'

export interface StoreDashboardWindowSearch {
  preset?: DateRangePreset
  startDate?: string
  endDate?: string
  /** `custom` : compStartDate/compEndDate font foi. `auto` (défaut) : comparaison calendaire. */
  comparableMode?: 'auto' | 'custom'
  compStartDate?: string
  compEndDate?: string
}

// Réimplémentation locale de `comparisonShiftDays` (~/components/dashboards/comparison) :
// décalage = 7 jours (semaine précédente) en dessous de 7 jours, sinon la durée elle-même.
const SAME_WEEKDAYS_MAX_WINDOW_DAYS = 6
function comparisonShiftDays(windowDays: number): number {
  return windowDays <= SAME_WEEKDAYS_MAX_WINDOW_DAYS ? 7 : windowDays
}

/**
 * Réimplémentation locale de `rangeFromStartThroughYesterday`
 * (`@gozoki/ui/components/ui/date-range-picker`), paramétrée par `today` (au lieu de
 * `DateTime.now()`) pour rester déterministe en test. `start: null` ⇒ « Toujours ».
 */
function rangeFromStartThroughYesterday(start: DateTime | null, today: DateTime): DateWindow {
  const end = today.minus({ days: 1 }).startOf('day')
  if (start === null) {
    return { start: STORE_DASHBOARD_ALL_TIME_SENTINEL, end: end.toISODate()! }
  }
  const from = start.startOf('day') > end ? end : start.startOf('day')
  return { start: from.toISODate()!, end: end.toISODate()! }
}

// Réimplémentation locale de `DEFAULT_PRESETS.getRange()` (même bibliothèque de presets que
// le Network Dashboard / RH), paramétrée par `today`.
function rangeForPreset(preset: DateRangePreset, today: DateTime): DateWindow {
  const day = today.startOf('day')
  switch (preset) {
    case 'yesterday': {
      const y = day.minus({ days: 1 }).toISODate()!
      return { start: y, end: y }
    }
    case 'today': {
      const iso = day.toISODate()!
      return { start: iso, end: iso }
    }
    case '7d': {
      return rangeFromStartThroughYesterday(day.minus({ days: 7 }), today)
    }
    case '30d': {
      return rangeFromStartThroughYesterday(day.minus({ days: 30 }), today)
    }
    case '3m': {
      return rangeFromStartThroughYesterday(day.minus({ months: 3 }), today)
    }
    case '12m': {
      return rangeFromStartThroughYesterday(day.minus({ months: 12 }), today)
    }
    case 'mtd': {
      return rangeFromStartThroughYesterday(day.startOf('month'), today)
    }
    case 'ytd': {
      return rangeFromStartThroughYesterday(day.startOf('year'), today)
    }
    case 'all': {
      return rangeFromStartThroughYesterday(null, today)
    }
  }
}

/** Fenêtre de période brute (sentinelle « Toujours » non résolue) : URL si présente, sinon preset. */
export function resolveStoreDashboardPeriodWindow(
  search: Pick<StoreDashboardWindowSearch, 'preset' | 'startDate' | 'endDate'>,
  today: DateTime,
): DateWindow {
  if (search.startDate !== undefined && search.endDate !== undefined) {
    return { start: search.startDate, end: search.endDate }
  }
  return rangeForPreset(search.preset ?? '7d', today)
}

/**
 * Comparaison calendaire automatique d'une période — réimplémentation locale de
 * `buildAutoCalendarComparisonRange` (`~/components/dashboards/filters/date-presets`) sur des
 * `DateWindow` (ISO) plutôt que des `Date`. « Toujours » (sentinelle) n'a pas de fenêtre
 * précédente sensée : comme le picker réseau, on renvoie la période inchangée (le point
 * d'appel résout alors la sentinelle une seule fois pour les deux fenêtres, cf.
 * `resolveStoreDashboardWindows`).
 */
export function buildStoreDashboardAutoComparisonWindow(period: DateWindow): DateWindow {
  if (period.start === STORE_DASHBOARD_ALL_TIME_SENTINEL) {
    return { ...period }
  }
  const start = DateTime.fromISO(period.start)
  const end = DateTime.fromISO(period.end)
  const durationDays = Math.max(1, Math.floor(end.diff(start, 'days').days) + 1)
  const shiftDays = comparisonShiftDays(durationDays)
  return {
    start: start.minus({ days: shiftDays }).toISODate()!,
    end: end.minus({ days: shiftDays }).toISODate()!,
  }
}

/**
 * Réimplémentation locale de `resolveAllTimeStartIso`
 * (`~/components/dashboards/filters/all-time-range`), restreinte au périmètre du dashboard
 * boutique (une boutique ou toutes, jamais de filtre d'ancienneté — le sélecteur boutique est
 * `storeSelectionMode="single"`). Date de première vente de la boutique la plus ancienne du
 * périmètre ; repli sur la sentinelle si la liste de boutiques n'est pas encore chargée.
 */
export function resolveStoreDashboardAllTimeStartIso(
  availableStores: NetworkDashboardAvailableStore[],
  storeIds: number[],
): string {
  const scoped =
    storeIds.length > 0
      ? availableStores.filter((store) => storeIds.includes(store.id))
      : availableStores
  const references = scoped.map((store) => store.ageReferenceDate).sort()
  return references[0] ?? STORE_DASHBOARD_ALL_TIME_SENTINEL
}

/**
 * Résout `{ period, comparison }` pour l'affichage/les requêtes : période = URL si présente
 * (`startDate`/`endDate`, sentinelle « Toujours » résolue via `availableStores`/`storeIds`),
 * sinon preset (défaut `7d`). Comparaison = dates explicites en mode `custom`, sinon fenêtre
 * calendaire automatique — qui hérite du même début résolu que la période quand celle-ci part
 * de « Toujours » (pas de fenêtre « précédente » sensée dans ce cas).
 */
export function resolveStoreDashboardWindows(
  search: StoreDashboardWindowSearch,
  today: DateTime,
  availableStores: NetworkDashboardAvailableStore[] = [],
  effectiveStoreIds: number[] = [],
): DashboardWindows {
  const rawPeriod = resolveStoreDashboardPeriodWindow(search, today)
  const resolvedStart =
    rawPeriod.start === STORE_DASHBOARD_ALL_TIME_SENTINEL
      ? resolveStoreDashboardAllTimeStartIso(availableStores, effectiveStoreIds)
      : rawPeriod.start
  const period: DateWindow = { start: resolvedStart, end: rawPeriod.end }

  if (search.comparableMode === 'custom' && search.compStartDate && search.compEndDate) {
    return { period, comparison: { start: search.compStartDate, end: search.compEndDate } }
  }

  const rawComparison = buildStoreDashboardAutoComparisonWindow(rawPeriod)
  const comparison =
    rawComparison.start === STORE_DASHBOARD_ALL_TIME_SENTINEL ? period : rawComparison
  return { period, comparison }
}

/**
 * Analogue de `hrPeriodQueriesEnabled` côté RH : tant que la période résolue est encore la
 * sentinelle « Toujours » (résolution en attente de `availableStores`, cf.
 * `resolveStoreDashboardAllTimeStartIso`) ET que la liste des boutiques n'est pas encore
 * chargée, les requêtes datées ne doivent PAS partir — elles enverraient sinon
 * `startDate = STORE_DASHBOARD_ALL_TIME_SENTINEL` (2020-01-01) au lieu de la vraie date de
 * première vente. Se déclenche typiquement avec une boutique unique sélectionnée : `storeIds`
 * est alors disponible immédiatement depuis l'URL, sans attendre `api.stores.index`.
 */
export function storeDashboardPeriodQueriesEnabled(
  periodStartIso: string,
  storesSettled: boolean,
): boolean {
  return periodStartIso !== STORE_DASHBOARD_ALL_TIME_SENTINEL || storesSettled
}

const shortDay = (iso: string) => DateTime.fromISO(iso).toFormat('dd/MM')

export const formatDayLong = (iso: string): string => DateTime.fromISO(iso).toFormat('dd/MM/yyyy')

export const formatWindowShort = (window: DateWindow): string =>
  `${shortDay(window.start)} → ${shortDay(window.end)}`

export const formatWindowLong = (window: DateWindow): string =>
  `${formatDayLong(window.start)} → ${formatDayLong(window.end)}`

/** Bornes JS `Date` locales (00:00 / 23:59:59.999) pour les endpoints qui attendent des `Date`. */
export const windowToDates = (window: DateWindow): { start: Date; end: Date } => ({
  start: DateTime.fromISO(window.start).startOf('day').toJSDate(),
  end: DateTime.fromISO(window.end).endOf('day').toJSDate(),
})
